import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { RotateCcw } from 'lucide-react'
import { api } from '@/lib/api'
import { optionGroupsQuery } from '@/lib/options'
import { rebuildOrder, reorderProductIds, type ProductLookup, type ReorderIssue } from '@/lib/reorder'
import { useCart } from '@/context/CartContext'
import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import { useToast } from '@/components/ui/toast'
import type { OptionGroup, Order, StorefrontProduct } from '@/types'

/**
 * Put a past order back in the cart, configured as it was.
 *
 * The rebuild is deliberately against TODAY's catalogue, not the old order's
 * numbers: prices, option lists and rush availability all come off a fresh
 * GET /storefront/products/{id}, so a re-ordered line is one the server will
 * still accept. What no longer exists is reported rather than quietly dropped.
 *
 * The new lines arrive ticked and everything already in the cart is unticked,
 * so a customer who was mid-way through another order loses nothing and buys
 * nothing by accident.
 */
export default function BuyAgainButton({ order, className }: { order: Order; className?: string }) {
  const cart = useCart()
  const toast = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [pending, setPending] = useState(false)
  const [issues, setIssues] = useState<ReorderIssue[] | null>(null)
  const [added, setAdded] = useState(0)

  // Nothing to rebuild from, and re-ordering an unpaid order would create a
  // second one for the same goods - that page offers "Complete payment".
  if (!order.items?.length || order.status === 'pending_payment') return null

  const run = async () => {
    if (pending) return
    setPending(true)

    try {
      const ids = reorderProductIds(order)
      const [groupsOutcome, ...productOutcomes] = await Promise.allSettled([
        queryClient.fetchQuery(optionGroupsQuery),
        ...ids.map((id) =>
          queryClient.fetchQuery({
            // The raw string ProductDetail keys on, so both share one entry.
            queryKey: ['storefront-product', String(id)],
            queryFn: async () => (await api.get<{ data: StorefrontProduct }>(`/storefront/products/${id}`)).data.data,
            // Somebody is waiting on a click, and a de-listed product 404s
            // deterministically, so a retry only buys a second of nothing.
            retry: false,
          }),
        ),
      ])

      if (groupsOutcome.status === 'rejected') {
        toast.error('Could not load the product options', 'Please try again in a moment.')
        return
      }

      // A 404 is a fact we can act on; anything else means we do not know, and
      // adding only the lines that resolved would make a retry duplicate them.
      const notFound = (reason: unknown) => axios.isAxiosError(reason) && reason.response?.status === 404
      if (productOutcomes.some((outcome) => outcome.status === 'rejected' && !notFound(outcome.reason))) {
        toast.error('Could not load your items', 'Please check your connection and try again.')
        return
      }

      const products = new Map<number, ProductLookup>(
        ids.map((id, i) => {
          const outcome = productOutcomes[i]
          return [id, outcome.status === 'fulfilled' ? { status: 'ok', product: outcome.value } : { status: 'unavailable' }]
        }),
      )

      const built = rebuildOrder(order, products, groupsOutcome.value as OptionGroup[])

      if (built.lines.length === 0) {
        setAdded(0)
        setIssues(built.issues)
        return
      }

      cart.addMany(built.lines, { selectOnly: true })

      if (built.issues.length === 0) {
        toast.success(
          'Added to your cart',
          `${built.lines.length} item(s) from ${order.number}, ticked and priced at today's prices.`,
        )
        navigate('/cart')
        return
      }

      // Navigating away from a partial result hides the warning the customer
      // needs, so the panel is the hand-off instead.
      setAdded(built.lines.length)
      setIssues(built.issues)
    } finally {
      setPending(false)
    }
  }

  const dropped = (issues ?? []).filter((issue) => issue.severity === 'dropped')
  const changed = (issues ?? []).filter((issue) => issue.severity === 'changed')

  return (
    <>
      <Button onClick={run} loading={pending} className={className}>
        <RotateCcw className="size-4" /> Buy again
      </Button>

      <Modal
        open={issues !== null}
        onClose={() => setIssues(null)}
        title={`Buy again - ${order.number}`}
        footer={
          <>
            <Button variant="outline" onClick={() => setIssues(null)}>
              Close
            </Button>
            {added > 0 && (
              <Button
                onClick={() => {
                  setIssues(null)
                  navigate('/cart')
                }}
              >
                Go to cart
              </Button>
            )}
          </>
        }
      >
        <p className="text-sm font-semibold">
          {added > 0 ? `${added} item(s) added to your cart.` : 'Nothing from this order could be added.'}
        </p>

        {dropped.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-bold uppercase tracking-wide text-warning">Not added</p>
            <ul className="mt-2 space-y-2">
              {dropped.map((issue, i) => (
                <li key={i} className="rounded-lg border border-foreground/10 bg-foreground/5 p-3 text-sm">
                  <p>{issue.message}</p>
                  {issue.productId !== null && (
                    <button
                      className="mt-1 text-xs font-semibold text-primary hover:underline"
                      onClick={() => {
                        setIssues(null)
                        navigate(`/p/${issue.productId}`)
                      }}
                    >
                      Configure it now
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {changed.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-bold uppercase tracking-wide text-foreground/50">Added, with changes</p>
            <ul className="mt-2 space-y-1.5 text-sm text-foreground/70">
              {changed.map((issue, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-foreground/30">-</span>
                  <span>{issue.message}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="mt-4 border-t border-foreground/10 pt-3 text-xs text-foreground/50">
          Prices, options and rush availability are today's, not the old order's.
        </p>
      </Modal>
    </>
  )
}
